/**
 * Prefetch utilities for React Query
 *
 * Provides bounded, debounced prefetching for:
 * - List detail pages (sidebar hover)
 * - Media detail pages (card hover)
 *
 * Key behaviors:
 * - Debounced: waits 150ms before prefetching (prevents spam on quick mouse moves)
 * - Bounded: only prefetches if not already in cache
 * - Uses SAME queryFn as detail page hooks (reuses apiFetch/API functions)
 */

import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';
import { getMovieDetailsEnriched, getTVDetailsEnriched } from '../api/tmdb';

// Debounce time before triggering prefetch (ms)
const PREFETCH_DEBOUNCE_MS = 150;

// Stale time for prefetched data (5 minutes - longer than query staleTime)
const PREFETCH_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Hook that returns a prefetch function for list detail pages.
 * Call onMouseEnter of sidebar list items.
 *
 * IMPORTANT: Prefetches use the SAME query key + sort as WatchlistPage (added_at desc)
 * to avoid cache poisoning that causes items to disappear on hover.
 *
 * Usage:
 * ```tsx
 * const prefetchList = useListPrefetch();
 *
 * <div onMouseEnter={() => prefetchList(listId)}>
 *   ...
 * </div>
 * ```
 */
export function useListPrefetch() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;

  // Track pending prefetch timer
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track last prefetched ID to avoid duplicate prefetches
  const lastPrefetchedRef = useRef<string | null>(null);

  // Canonical sort params - MUST match WatchlistPage defaults
  const CANONICAL_SORT_BY = 'added_at';
  const CANONICAL_SORT_ORDER = 'desc';

  const prefetch = useCallback(
    (listId: string) => {
      // Clear any pending prefetch
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Skip if already prefetched recently
      if (lastPrefetchedRef.current === listId) {
        return;
      }

      // Build canonical query key - SAME as WatchlistPage uses
      const itemsQueryKey = [
        ...queryKeys.lists.itemsBase(listId),
        { sortBy: CANONICAL_SORT_BY, sortOrder: CANONICAL_SORT_ORDER },
      ];

      // Check if items cache already exists (any sort variant)
      const existingItemsCache = queryClient.getQueryCache().findAll({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.length >= 3 &&
          query.queryKey[0] === 'lists' &&
          query.queryKey[1] === listId &&
          query.queryKey[2] === 'items',
      });
      if (existingItemsCache.some((q) => q.state.data)) {
        return; // Already have items data, no need to prefetch
      }

      // Debounced prefetch
      timerRef.current = setTimeout(() => {
        lastPrefetchedRef.current = listId;

        // Prefetch as infinite query to match WatchlistPage's useWatchlistItemsInfinite
        queryClient.prefetchInfiniteQuery({
          queryKey: itemsQueryKey,
          queryFn: async () => {
            const data = await apiFetch<{
              watchlist: unknown;
              items: unknown[];
              pagination: { page: number; limit: number; total: number | null; has_more: boolean };
            }>(`/watchlists/${listId}?page=1&limit=100&sort_by=${CANONICAL_SORT_BY}&sort_order=${CANONICAL_SORT_ORDER}`, {
              token,
            });
            return {
              items: data.items || [],
              pagination: data.pagination || { page: 1, limit: 100, total: null, has_more: false },
              watchlist: data.watchlist,
            };
          },
          initialPageParam: 1,
          staleTime: PREFETCH_STALE_TIME_MS,
        });

        timerRef.current = null;
      }, PREFETCH_DEBOUNCE_MS);
    },
    [queryClient, token]
  );

  // Cancel function for cleanup (call on mouse leave)
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { prefetch, cancel };
}

/**
 * Hook that returns a prefetch function for movie detail pages.
 * Call onMouseEnter of movie cards.
 * Uses same queryFn as MovieDetail (getMovieDetailsEnriched).
 */
export function useMoviePrefetch() {
  const queryClient = useQueryClient();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPrefetchedRef = useRef<number | null>(null);

  const prefetch = useCallback(
    (movieId: number) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (lastPrefetchedRef.current === movieId) {
        return;
      }

      const cached = queryClient.getQueryData(queryKeys.movie.detail(movieId));
      if (cached) {
        return;
      }

      timerRef.current = setTimeout(() => {
        lastPrefetchedRef.current = movieId;

        // Use same queryFn as MovieDetail component
        queryClient.prefetchQuery({
          queryKey: queryKeys.movie.detail(movieId),
          queryFn: () => getMovieDetailsEnriched(movieId),
          staleTime: PREFETCH_STALE_TIME_MS,
        });

        timerRef.current = null;
      }, PREFETCH_DEBOUNCE_MS);
    },
    [queryClient]
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { prefetch, cancel };
}

/**
 * Hook that returns a prefetch function for TV detail pages.
 * Call onMouseEnter of TV cards.
 * Uses same queryFn as TVDetail (getTVDetailsEnriched).
 */
export function useTVPrefetch() {
  const queryClient = useQueryClient();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPrefetchedRef = useRef<number | null>(null);

  const prefetch = useCallback(
    (tvId: number) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (lastPrefetchedRef.current === tvId) {
        return;
      }

      const cached = queryClient.getQueryData(queryKeys.tv.detail(tvId));
      if (cached) {
        return;
      }

      timerRef.current = setTimeout(() => {
        lastPrefetchedRef.current = tvId;

        // Use same queryFn as TVDetail component
        queryClient.prefetchQuery({
          queryKey: queryKeys.tv.detail(tvId),
          queryFn: () => getTVDetailsEnriched(tvId),
          staleTime: PREFETCH_STALE_TIME_MS,
        });

        timerRef.current = null;
      }, PREFETCH_DEBOUNCE_MS);
    },
    [queryClient]
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { prefetch, cancel };
}
